import { BusyContent, Alert, Button, PageHeader, Skeleton, Surface } from '../components/ui/Primitives';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './KnowledgeBase.css';
import Website from '../components/SettingsComponents/Website';
import BusinessName from '../components/SettingsComponents/BusinessName';
import IndustrySelector from '../components/SettingsComponents/IndustrySelector';
import BusinessLocation from '../components/SettingsComponents/BusinessLocation';
import BusinessHours from '../components/SettingsComponents/BusinessHours';
import BusinessPhone from '../components/SettingsComponents/BusinessPhone';
import BusinessBio from '../components/SettingsComponents/BusinessBio';
import TeamBio from '../components/SettingsComponents/TeamBio';
import BusinessServices from '../components/SettingsComponents/BusinessServices';
import BusinessFAQ from '../components/SettingsComponents/BusinessFAQ';
import TimezoneSelector from '../components/SettingsComponents/TimezoneSelector';
import {
  KnowledgeSourceComposer,
  KnowledgeSourceDeleteDialog,
  KnowledgeSourceLibrary,
  KnowledgeNoteEditor,
  KNOWLEDGE_SUGGESTION_FIELDS,
  SectionSuggestionReview,
  SuggestionReview,
  knowledgeErrorMessage,
} from '../components/KnowledgeSources';
import {
  createKnowledgeNote,
  createKnowledgeUrl,
  deleteKnowledgeSource,
  dismissAllKnowledgeSuggestions,
  dismissKnowledgeSuggestions,
  getConsolidatedKnowledgeSuggestions,
  getKnowledgeSources,
  getKnowledgeNoteContent,
  getKnowledgeSuggestions,
  publishKnowledgeProfile,
  reingestKnowledgeFile,
  refreshKnowledgeSource,
  replaceKnowledgeNote,
  testKnowledge,
  updateKnowledgeSource,
  uploadKnowledgeFiles,
} from '../services/knowledgeApi';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import flashMessage from '../components/HelperFunctions';
import { memberProfileFromResponse } from '../utils/sessionData';
import { supportedIndustryLabel } from '../utils/industries';
import { profileSuggestionIsDuplicate } from '../utils/profileSuggestions';


const PROFILE_SECTIONS = [
  {
    key: 'general',
    title: 'General business',
    description: 'The identity and short description your Receptionist uses most often.',
  },
  {
    key: 'hours_location',
    title: 'Hours & location',
    description: 'Canonical contact details, location, and opening hours.',
  },
  {
    key: 'services_pricing',
    title: 'Services & pricing',
    description: 'Keep the summary concise and manage menus, catalogs, or detailed pricing in Supporting knowledge.',
  },
  {
    key: 'team',
    title: 'Team',
    description: 'Help callers understand who works with your customers.',
  },
  {
    key: 'policies_faqs',
    title: 'FAQs & policies',
    description: 'Common answers, policies, and other approved caller guidance.',
  },
];
const ALL_SOURCE_SECTION_KEYS = PROFILE_SECTIONS.map(section => section.key);
const ALL_SUGGESTIONS_BUSY_ID = 'all-suggestions';
const APPLY_SECTION_BUSY_ID = 'apply-section';
const CONSOLIDATED_SUGGESTION_SOURCE_ID = 'consolidated-profile-suggestions';

const PROFILE_SECTION_ICON_PATHS = {
  general: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2" /></>,
  hours_location: <><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></>,
  services_pricing: <><path d="M8 6h12M8 12h12M8 18h12" /><path d="M4 6h.01M4 12h.01M4 18h.01" /></>,
  team: <><path d="M16 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="3" /><path d="M17 11a3 3 0 0 1 3 3v2" /></>,
  policies_faqs: <><circle cx="12" cy="12" r="9" /><path d="M9.8 9a2.4 2.4 0 1 1 3.6 2.1c-.9.5-1.4 1-1.4 2.1M12 17h.01" /></>,
};

function consolidatedSuggestionSource(summary = {}) {
  return {
    ...summary,
    id: CONSOLIDATED_SUGGESTION_SOURCE_ID,
    title: 'Supporting knowledge',
    kind: 'consolidated',
    sectionKeys: PROFILE_SECTIONS.map(section => section.key),
    suggestionCount: summary.suggestionCount || 0,
    suggestionFields: summary.suggestionFields || [],
    suggestionPreviews: summary.suggestionPreviews || {},
  };
}

function ProfileSectionIcon({ sectionKey }) {
  return <span className={`knowledge-profile-section-icon ${sectionKey}`} aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{PROFILE_SECTION_ICON_PATHS[sectionKey]}</svg></span>;
}

function publishedAtLabel(value) {
  if (!value) return '';
  const publishedAt = new Date(value);
  if (Number.isNaN(publishedAt.getTime())) return '';
  return `Last published ${new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(publishedAt)}`;
}

function hideSuggestionFields(source, fieldKeys) {
  const hidden = new Set(fieldKeys || []);
  const suggestionPreviews = { ...(source.suggestionPreviews || {}) };
  hidden.forEach(key => { suggestionPreviews[key] = ''; });
  const suggestionFields = (source.suggestionFields || []).filter(key => !hidden.has(key));
  return {
    ...source,
    suggestionPreviews,
    suggestionFields,
    suggestionCount: suggestionFields.length,
  };
}

function sectionSuggestions(source, sectionKey, organization) {
  if (!source?.suggestionCount || !source.sectionKeys?.includes(sectionKey)) return [];
  return KNOWLEDGE_SUGGESTION_FIELDS.filter(field => (
    field.sectionKey === sectionKey && source.suggestionPreviews?.[field.key]
  )).filter(field => {
    const preview = source.suggestionPreviews[field.key];
    // A trailing ellipsis means this is only a server-generated excerpt. The
    // backend compares the complete value; avoid hiding a potentially richer
    // suggestion based on a partial preview.
    return preview.endsWith('…') || !profileSuggestionIsDuplicate(
      field.key,
      preview,
      organization?.[field.organizationKey],
    );
  }).map(field => ({
      key: field.key,
      label: field.label,
      preview: field.key === 'industry'
        ? supportedIndustryLabel(source.suggestionPreviews[field.key])
        : source.suggestionPreviews[field.key],
    }));
}


function ProfileSuggestion({ source, suggestion, busySourceId, sectionBusy, onApply, onDismiss, onLoadFull }) {
  const [expanded, setExpanded] = useState(false);
  const [fullContent, setFullContent] = useState('');
  const [loadingFull, setLoadingFull] = useState(false);
  const [fullError, setFullError] = useState('');
  const [canExpand, setCanExpand] = useState(false);
  const previewRef = useRef(null);
  const applying = busySourceId === `suggestion:${source.id}:${suggestion.key}`;
  const sourceBusy = busySourceId === source.id || String(busySourceId || '').startsWith(`suggestion:${source.id}:`);

  useEffect(() => {
    if (expanded) return undefined;
    const preview = previewRef.current;
    if (!preview) return undefined;
    const serverTruncated = String(suggestion.preview || '').endsWith('…');
    const measure = () => setCanExpand(
      serverTruncated || preview.scrollHeight > preview.clientHeight + 1,
    );
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(preview);
    return () => observer.disconnect();
  }, [expanded, suggestion.preview]);

  async function toggleExpanded() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    if (!fullContent) {
      setLoadingFull(true);
      setFullError('');
      try {
        setFullContent(await onLoadFull(source, suggestion.key));
      } catch (error) {
        setFullError(knowledgeErrorMessage(error, 'The full suggestion could not be loaded.'));
      } finally {
        setLoadingFull(false);
      }
    }
    setExpanded(true);
  }

  return (
    <div className={`knowledge-profile-suggestion${canExpand ? ' expandable' : ''}${expanded ? ' expanded' : ''}`}>
      <button className="knowledge-suggestion-review" type="button" disabled={sourceBusy || sectionBusy} onClick={() => onApply(source, suggestion)}><strong><BusyContent busy={applying} label="Applying suggestion…">{suggestion.label}</BusyContent></strong><span ref={previewRef}>{expanded ? (fullError || fullContent) : suggestion.preview}</span></button>
      <button className="knowledge-suggestion-dismiss" type="button" aria-label={`Dismiss ${suggestion.label} suggestion`} disabled={sourceBusy || sectionBusy} onClick={() => onDismiss(source, suggestion.key)}>{busySourceId === source.id ? <span className="knowledge-spinner" aria-hidden="true" /> : '×'}</button>
      {canExpand ? <button className="knowledge-suggestion-expand" type="button" aria-expanded={expanded} disabled={sourceBusy || sectionBusy || loadingFull} onClick={toggleExpanded}><BusyContent busy={loadingFull} label={"Loading…"}>{expanded ? 'Show less' : 'View full suggestion'}</BusyContent></button> : null}
    </div>
  );
}


function ProfileSection({ section, title, suggestionSources, busySourceId, onApply, onApplyAll, onDismiss, onDismissAll, onLoadFull, children }) {
  const hasSuggestions = suggestionSources.length > 0;
  const dismissingSection = busySourceId === `${ALL_SUGGESTIONS_BUSY_ID}:${section.key}`;
  const loadingSectionReview = busySourceId === `${APPLY_SECTION_BUSY_ID}:${section.key}`;
  const anyBulkBusy = String(busySourceId || '').startsWith(ALL_SUGGESTIONS_BUSY_ID);
  const sectionBusy = anyBulkBusy || loadingSectionReview;
  return (
    <Surface className={`knowledge-profile-section${hasSuggestions ? ' has-suggestions' : ''}`}>
      <div className="knowledge-profile-section-heading">
        <ProfileSectionIcon sectionKey={section.key} />
        <div><h2>{title || section.title}</h2><p>{section.description}</p></div>
      </div>
      <div className="knowledge-section-fields">{children}</div>
      {hasSuggestions ? <div className="knowledge-profile-suggestions" aria-live="polite">
          <div className="knowledge-profile-suggestions-heading"><span>Suggested from your sources</span><div className="knowledge-profile-suggestion-actions"><Button variant="quiet" size="sm" loading={loadingSectionReview} loadingLabel="Loading…" disabled={anyBulkBusy || dismissingSection} onClick={() => onApplyAll(section, suggestionSources)}>Apply all</Button><Button variant="quiet" size="sm" loading={dismissingSection} loadingLabel="Dismissing…" disabled={(anyBulkBusy && !dismissingSection) || loadingSectionReview} onClick={() => onDismissAll(section.key)}>Dismiss all</Button></div></div>
          {suggestionSources.flatMap(({ source, suggestions }) => suggestions.map(suggestion => <ProfileSuggestion key={`${source.id}-${suggestion.key}`} source={source} suggestion={suggestion} busySourceId={busySourceId} sectionBusy={sectionBusy} onApply={onApply} onDismiss={onDismiss} onLoadFull={onLoadFull} />))}
        </div> : null}
    </Surface>
  );
}


function KnowledgeBase({ organization, orgMember }) {
  const [updatedOrgMember, setUpdatedOrgMember] = useState(orgMember);
  const [updatedOrganization, setUpdatedOrganization] = useState(organization);
  const [isLoading, setIsLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sources, setSources] = useState([]);
  const [profileSuggestions, setProfileSuggestions] = useState(null);
  const [capability, setCapability] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [sourceError, setSourceError] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [busySourceId, setBusySourceId] = useState(null);
  const [busySourceLabel, setBusySourceLabel] = useState('');
  const [suggestionReview, setSuggestionReview] = useState(null);
  const [sectionSuggestionReview, setSectionSuggestionReview] = useState(null);
  const [noteEditor, setNoteEditor] = useState(null);
  const [sourcePendingDeletion, setSourcePendingDeletion] = useState(null);
  const [profileFieldRevisions, setProfileFieldRevisions] = useState({});
  const [lastPublishedAt, setLastPublishedAt] = useState(null);
  const navigate = useNavigate();
  const sourceStatusRef = useRef(new Map());
  const pendingAppliedSuggestionFieldsRef = useRef(new Set());

  const loadSources = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setSourcesLoading(true);
    try {
      const result = await getKnowledgeSources();
      const nextSources = (result.knowledgeSources || []).map(source => (
        hideSuggestionFields(source, pendingAppliedSuggestionFieldsRef.current)
      ));
      const nextProfileSuggestions = result.profileSuggestions
        ? hideSuggestionFields(
          consolidatedSuggestionSource(result.profileSuggestions),
          pendingAppliedSuggestionFieldsRef.current,
        )
        : null;
      const completedWithSuggestions = nextSources.filter(source => {
        const previousStatus = sourceStatusRef.current.get(source.id);
        return ['queued', 'processing'].includes(previousStatus)
          && source.latestVersionStatus === 'ready'
          && source.suggestionCount > 0;
      });
      sourceStatusRef.current = new Map(nextSources.map(source => [
        source.id,
        source.latestVersionStatus || source.status,
      ]));
      setSources(nextSources);
      setProfileSuggestions(nextProfileSuggestions);
      setLastPublishedAt(result.profilePublishedAt || null);
      setCapability(result.capability || { canManage: false });
      setSourceError('');
      if (completedWithSuggestions.length) {
        const suggestionCount = completedWithSuggestions.reduce(
          (total, source) => total + source.suggestionCount,
          0,
        );
        flashMessage(
          'success',
          `${suggestionCount} profile suggestion${suggestionCount === 1 ? '' : 's'} ready to review.`,
        );
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setCapability(null);
      } else if (error?.response?.status === 401) {
        localStorage.removeItem('jwt_authorization');
        navigate('/');
      } else {
        setSourceError(knowledgeErrorMessage(error, 'Supporting sources could not be loaded.'));
      }
    } finally {
      if (!quiet) setSourcesLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    async function fetchUserData() {
      setIsLoading(true);
      const jwtToken = localStorage.getItem('jwt_authorization');
      if (!jwtToken) { navigate('/'); return; }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_HOST}/user`,
          { jwt_token: jwtToken },
        );
        if (response.status === 200) setUpdatedOrgMember(memberProfileFromResponse(response.data));
        const orgResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_HOST}/organization`,
          { jwt_token: jwtToken },
        );
        if (orgResponse.status === 200) setUpdatedOrganization(orgResponse.data.organization);
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('jwt_authorization');
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    }
    fetchUserData();
    loadSources();
  }, [loadSources, navigate]);

  const hasProcessingSources = useMemo(() => sources.some(source => (
    ['queued', 'processing'].includes(source.status)
    || ['queued', 'processing'].includes(source.latestVersionStatus)
  )), [sources]);

  const suggestionSourcesBySection = useMemo(() => Object.fromEntries(
    PROFILE_SECTIONS.map(section => {
      if (profileSuggestions) {
        const suggestions = sectionSuggestions(
          profileSuggestions,
          section.key,
          updatedOrganization,
        );
        return [section.key, suggestions.length ? [{
          source: profileSuggestions,
          suggestions,
        }] : []];
      }
      const includedFields = new Set();
      const includedValues = new Set();
      const sectionSources = sources.map(source => ({
        source,
        suggestions: sectionSuggestions(source, section.key, updatedOrganization).filter(suggestion => {
          const normalizedValue = suggestion.preview.toLowerCase().replace(/\s+/g, ' ').trim();
          if (includedFields.has(suggestion.key) || includedValues.has(normalizedValue)) return false;
          includedFields.add(suggestion.key);
          includedValues.add(normalizedValue);
          return true;
        }),
      })).filter(item => item.suggestions.length > 0);
      return [section.key, sectionSources];
    }),
  ), [profileSuggestions, sources, updatedOrganization]);

  useEffect(() => {
    if (!hasProcessingSources) return undefined;
    const timer = window.setInterval(() => loadSources({ quiet: true }), 3500);
    return () => window.clearInterval(timer);
  }, [hasProcessingSources, loadSources]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const warnBeforeLeaving = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [isDirty]);

  useEffect(() => {
    if (!isLoading && updatedOrgMember) {
      const iframe = document.getElementById('chat-iframe');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'page.navigation', page: 'Knowledge' }, '*');
      }
    }
  }, [isLoading, updatedOrgMember]);

  function handleChildChange(fieldKey, value) {
    const field = KNOWLEDGE_SUGGESTION_FIELDS.find(item => item.key === fieldKey);
    if (field) {
      setUpdatedOrganization(current => ({
        ...current,
        [field.organizationKey]: value,
      }));
    }
    if (!isDirty) setIsDirty(true);
  }

  async function handleSave() {
    setIsSaving(true);
    flashMessage('info', 'Publishing your profile. This will just take a moment...');
    try {
      const website = document.getElementsByClassName('website-text')[0].value;
      const businessName = document.getElementsByClassName('business-name-text')[0].value;
      const industry = document.getElementsByClassName('industry-selector')[0].value;
      const businessLocation = document.getElementsByClassName('business-location-text')[0].value;
      const businessHours = document.getElementsByClassName('business-hours-text')[0].value;
      const businessPhone = document.getElementsByClassName('business-phone-text')[0].value;
      const businessBio = document.getElementsByClassName('business-bio-text')[0].value;
      const teamBio = document.getElementsByClassName('team-bio-text')[0].value;
      const businessServices = document.getElementsByClassName('business-services-text')[0].value;
      const businessFAQ = document.getElementsByClassName('business-faq-text')[0].value;
      const timezone = document.getElementsByClassName('knowledge-timezone-selector')[0].value;
      const result = await publishKnowledgeProfile({
        businessWebsite: website,
        businessName,
        industry,
        businessLocation,
        businessHours,
        businessPhoneNumber: businessPhone,
        businessBio,
        staffBio: teamBio,
        businessServices,
        businessFaq: businessFAQ,
        timezone,
      }, [...pendingAppliedSuggestionFieldsRef.current]);
      pendingAppliedSuggestionFieldsRef.current.clear();
      setUpdatedOrganization(result.organization);
      setLastPublishedAt(result.publishedAt || new Date().toISOString());
      await loadSources({ quiet: true });
      flashMessage('success', 'Profile published to your Receptionist.');
      setIsDirty(false);
    } catch (error) {
      flashMessage('error', knowledgeErrorMessage(error, 'The profile could not be published.'));
    } finally {
      setIsSaving(false);
    }
  }

  async function createSource(kind, values) {
    if (kind === 'file') await uploadKnowledgeFiles(values.files, ALL_SOURCE_SECTION_KEYS);
    if (kind === 'url') await createKnowledgeUrl({
      title: values.title,
      url: values.url,
      sectionKeys: ALL_SOURCE_SECTION_KEYS,
    });
    if (kind === 'note') await createKnowledgeNote({
      title: values.title,
      content: values.content,
      sectionKeys: ALL_SOURCE_SECTION_KEYS,
    });
    await loadSources({ quiet: true });
    flashMessage('success', 'Source added. Sayable is preparing it now.');
  }

  async function updateSource(sourceId, changes) {
    setBusySourceId(sourceId);
    setBusySourceLabel('Saving changes…');
    try {
      const updated = await updateKnowledgeSource(sourceId, changes);
      setSources(current => current.map(source => source.id === sourceId ? updated : source));
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error));
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function reingestSource(sourceId) {
    setBusySourceId(sourceId);
    setBusySourceLabel('Starting update…');
    try {
      const updated = await reingestKnowledgeFile(sourceId, ALL_SOURCE_SECTION_KEYS);
      setSources(current => current.map(source => source.id === sourceId ? updated : source));
      flashMessage('success', 'Reprocessing queued. Sayable is finding and organizing all relevant knowledge.');
      return true;
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'The document could not be re-ingested.'));
      return false;
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function refreshSource(sourceId) {
    setBusySourceId(sourceId);
    setBusySourceLabel('Refreshing website…');
    try {
      const updated = await refreshKnowledgeSource(sourceId);
      setSources(current => current.map(source => source.id === sourceId ? updated : source));
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'The website could not be refreshed.'));
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function editNote(source) {
    setBusySourceId(source.id);
    setBusySourceLabel('Opening note…');
    try {
      const content = await getKnowledgeNoteContent(source.id);
      setNoteEditor({ source, content });
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'The note could not be loaded.'));
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function saveNote(sourceId, content) {
    const updated = await replaceKnowledgeNote(sourceId, content);
    setSources(current => current.map(source => source.id === sourceId ? updated : source));
    flashMessage('success', 'Note update saved and queued for processing.');
  }

  async function removeSource(source) {
    setBusySourceId(source.id);
    setBusySourceLabel('Deleting source…');
    try {
      await deleteKnowledgeSource(source.id);
      setSources(current => current.filter(item => item.id !== source.id));
      flashMessage('success', `${source.title} was deleted.`);
      return true;
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'The source could not be deleted.'));
      return false;
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function reviewSuggestions(source) {
    setBusySourceId(source.id);
    setBusySourceLabel('Loading suggestions…');
    try {
      const result = await getKnowledgeSuggestions(source.id);
      setSuggestionReview({ source, suggestions: result.suggestions || {} });
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'Suggestions could not be loaded.'));
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  function stageProfileSuggestionFields(fields) {
    const normalizedKeys = Object.keys(fields || {});
    setUpdatedOrganization(current => {
      const next = { ...current };
      normalizedKeys.forEach(fieldKey => {
        const field = KNOWLEDGE_SUGGESTION_FIELDS.find(item => item.key === fieldKey);
        if (field) next[field.organizationKey] = fields[fieldKey];
      });
      return next;
    });
    setProfileFieldRevisions(current => {
      const next = { ...current };
      normalizedKeys.forEach(fieldKey => {
        next[fieldKey] = (next[fieldKey] || 0) + 1;
      });
      return next;
    });
    normalizedKeys.forEach(fieldKey => pendingAppliedSuggestionFieldsRef.current.add(fieldKey));
    setSources(current => current.map(source => hideSuggestionFields(source, normalizedKeys)));
    setProfileSuggestions(current => (
      current ? hideSuggestionFields(current, normalizedKeys) : current
    ));
    setIsDirty(true);
  }

  async function applySuggestions(sourceId, fields) {
    void sourceId;
    stageProfileSuggestionFields(fields);
    flashMessage('success', 'Suggestion added to your unpublished profile.');
  }

  async function suggestionDetails(source) {
    if (source.id === CONSOLIDATED_SUGGESTION_SOURCE_ID) {
      return getConsolidatedKnowledgeSuggestions();
    }
    return getKnowledgeSuggestions(source.id);
  }

  async function applySingleSuggestion(source, suggestion) {
    setBusySourceId(`suggestion:${source.id}:${suggestion.key}`);
    setBusySourceLabel('Applying suggestion…');
    try {
      const details = await suggestionDetails(source);
      const value = details.suggestions?.[suggestion.key];
      if (!value) throw new Error('Suggestion is no longer available.');
      await applySuggestions(source.id, { [suggestion.key]: value });
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, error.message || 'The suggestion could not be applied.'));
      await loadSources({ quiet: true });
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function loadFullSuggestion(source, fieldKey) {
    const details = await suggestionDetails(source);
    const value = details.suggestions?.[fieldKey];
    if (!value) throw new Error('Suggestion is no longer available.');
    return value;
  }

  async function reviewSectionSuggestions(section, suggestionSources) {
    setBusySourceId(`${APPLY_SECTION_BUSY_ID}:${section.key}`);
    setBusySourceLabel('Loading section suggestions…');
    try {
      const detailedSources = await Promise.all(suggestionSources.map(async item => ({
        ...item,
        values: (await suggestionDetails(item.source)).suggestions || {},
      })));
      const entries = [];
      const includedFields = new Set();
      detailedSources.forEach(({ source, suggestions, values }) => {
        suggestions.forEach(suggestion => {
          const field = KNOWLEDGE_SUGGESTION_FIELDS.find(item => item.key === suggestion.key);
          const value = values[suggestion.key];
          if (!field || !value || includedFields.has(field.key)) return;
          includedFields.add(field.key);
          entries.push({
            ...field,
            id: `${source.id}:${field.key}`,
            source,
            value,
          });
        });
      });
      if (!entries.length) {
        await loadSources({ quiet: true });
        flashMessage('info', 'These suggestions are no longer available.');
        return;
      }
      setSectionSuggestionReview({ section, entries });
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'Section suggestions could not be loaded.'));
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function applySectionSuggestions(entries) {
    stageProfileSuggestionFields(Object.fromEntries(
      entries.map(entry => [entry.key, entry.value]),
    ));
    flashMessage('success', 'Section suggestions added to your unpublished profile.');
  }

  async function dismissSuggestion(source, fieldKey) {
    setBusySourceId(source.id);
    setBusySourceLabel('Dismissing suggestion…');
    try {
      if (source.id === CONSOLIDATED_SUGGESTION_SOURCE_ID) {
        const result = await dismissAllKnowledgeSuggestions(null, [fieldKey]);
        const updatedById = new Map(
          (result.knowledgeSources || []).map(item => [item.id, item]),
        );
        setSources(current => current.map(item => updatedById.get(item.id) || item));
        setProfileSuggestions(result.profileSuggestions
          ? consolidatedSuggestionSource(result.profileSuggestions)
          : null);
        return;
      }
      const updated = await dismissKnowledgeSuggestions(source.id, [fieldKey]);
      setSources(current => current.map(item => item.id === source.id ? updated : item));
      await loadSources({ quiet: true });
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'The suggestion could not be dismissed.'));
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  async function dismissAllSuggestions(sectionKey = null) {
    setBusySourceId(sectionKey ? `${ALL_SUGGESTIONS_BUSY_ID}:${sectionKey}` : ALL_SUGGESTIONS_BUSY_ID);
    setBusySourceLabel(sectionKey ? 'Dismissing section suggestions…' : 'Dismissing all suggestions…');
    try {
      const result = await dismissAllKnowledgeSuggestions(sectionKey);
      const updatedById = new Map(
        (result.knowledgeSources || []).map(source => [source.id, source]),
      );
      setSources(current => current.map(source => updatedById.get(source.id) || source));
      setProfileSuggestions(result.profileSuggestions
        ? consolidatedSuggestionSource(result.profileSuggestions)
        : null);
      flashMessage('success', sectionKey ? 'Section suggestions dismissed.' : 'All profile suggestions dismissed.');
    } catch (error) {
      setSourceError(knowledgeErrorMessage(error, 'The suggestions could not be dismissed.'));
      await loadSources({ quiet: true });
    } finally {
      setBusySourceId(null);
      setBusySourceLabel('');
    }
  }

  if (isLoading || !updatedOrgMember) {
    return <div className="page-content knowledge-loading"><Skeleton width="45%" height={42} /><Skeleton height={420} /></div>;
  }

  return (
    <div className="page-content mobile-admin" style={{ flexDirection: 'column' }}>
      <div className="knowledge-content">
        <PageHeader
          eyebrow="Knowledge"
          title="Teach Sayable about your business"
          description="Keep essential facts concise, then add detailed documents, webpages, and notes alongside the profile."
        />
        {sourceError ? <Alert tone="danger" actions={<Button size="sm" variant="secondary" onClick={() => setSourceError('')}>Dismiss</Button>}>{sourceError}</Alert> : null}
        <div className="knowledge-workspace-grid">
          <aside className="knowledge-supporting-block" aria-label="Profile publishing and supporting knowledge">
            <Surface className={`knowledge-publish-card${isDirty ? ' has-unpublished' : ''}`}>
              <div className="knowledge-publish-status"><span aria-hidden="true" /><div><small>Profile status</small><strong>{isDirty ? 'Unpublished changes' : 'Profile is live'}</strong></div></div>
              <h2>{isDirty ? 'Ready to publish?' : 'Your Receptionist is up to date'}</h2>
              <p>{isDirty ? 'Publish these profile changes to make them available to your Receptionist.' : 'Make an edit or apply a suggestion when you want to update the live profile.'}</p>
              <Button data-say-id="knowledge-base-publish-button" onClick={handleSave} loading={isSaving} loadingLabel="Publishing profile…" disabled={!isDirty}>Publish profile</Button>
              {publishedAtLabel(lastPublishedAt) ? <small className="knowledge-published-at">{publishedAtLabel(lastPublishedAt)}</small> : null}
            </Surface>
            {capability ? <KnowledgeSourceLibrary
              sources={sources}
              loading={sourcesLoading}
              capability={capability}
              busySourceId={busySourceId}
              busySourceLabel={busySourceLabel}
              onAdd={() => setComposerOpen(true)}
              onUpdate={updateSource}
              onReingest={reingestSource}
              onEditNote={editNote}
              onRefresh={refreshSource}
              onDelete={setSourcePendingDeletion}
              onReviewSuggestions={reviewSuggestions}
              onTest={testKnowledge}
            /> : null}
          </aside>
          <div className="knowledge-profile-sections">
            <ProfileSection section={PROFILE_SECTIONS[0]} title="Business essentials" suggestionSources={suggestionSourcesBySection.general} busySourceId={busySourceId} onApply={applySingleSuggestion} onApplyAll={reviewSectionSuggestions} onDismiss={dismissSuggestion} onDismissAll={dismissAllSuggestions} onLoadFull={loadFullSuggestion}><Website organization={updatedOrganization} onChange={value => handleChildChange('businessWebsite', value)} /><BusinessName key={`business-name-${profileFieldRevisions.businessName || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessName', value)} /><IndustrySelector key={`industry-${profileFieldRevisions.industry || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('industry', value)} /><BusinessBio key={`business-bio-${profileFieldRevisions.businessBio || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessBio', value)} /></ProfileSection>
            <ProfileSection section={PROFILE_SECTIONS[1]} suggestionSources={suggestionSourcesBySection.hours_location} busySourceId={busySourceId} onApply={applySingleSuggestion} onApplyAll={reviewSectionSuggestions} onDismiss={dismissSuggestion} onDismissAll={dismissAllSuggestions} onLoadFull={loadFullSuggestion}><BusinessLocation key={`business-location-${profileFieldRevisions.businessLocation || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessLocation', value)} /><BusinessHours key={`business-hours-${profileFieldRevisions.businessHours || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessHours', value)} /><BusinessPhone key={`business-phone-${profileFieldRevisions.businessPhoneNumber || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessPhoneNumber', value)} /><TimezoneSelector key={`timezone-${profileFieldRevisions.timezone || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('timezone', value)} /></ProfileSection>
            <ProfileSection section={PROFILE_SECTIONS[2]} suggestionSources={suggestionSourcesBySection.services_pricing} busySourceId={busySourceId} onApply={applySingleSuggestion} onApplyAll={reviewSectionSuggestions} onDismiss={dismissSuggestion} onDismissAll={dismissAllSuggestions} onLoadFull={loadFullSuggestion}><BusinessServices key={`business-services-${profileFieldRevisions.businessServices || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessServices', value)} /></ProfileSection>
            <ProfileSection section={PROFILE_SECTIONS[3]} suggestionSources={suggestionSourcesBySection.team} busySourceId={busySourceId} onApply={applySingleSuggestion} onApplyAll={reviewSectionSuggestions} onDismiss={dismissSuggestion} onDismissAll={dismissAllSuggestions} onLoadFull={loadFullSuggestion}><TeamBio key={`team-bio-${profileFieldRevisions.staffBio || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('staffBio', value)} /></ProfileSection>
            <ProfileSection section={PROFILE_SECTIONS[4]} suggestionSources={suggestionSourcesBySection.policies_faqs} busySourceId={busySourceId} onApply={applySingleSuggestion} onApplyAll={reviewSectionSuggestions} onDismiss={dismissSuggestion} onDismissAll={dismissAllSuggestions} onLoadFull={loadFullSuggestion}><BusinessFAQ key={`business-faq-${profileFieldRevisions.businessFaq || 0}`} organization={updatedOrganization} onChange={value => handleChildChange('businessFaq', value)} /></ProfileSection>
          </div>
        </div>
      </div>
      {composerOpen ? <KnowledgeSourceComposer capability={capability} onClose={() => setComposerOpen(false)} onCreate={createSource} /> : null}
      {noteEditor ? <KnowledgeNoteEditor source={noteEditor.source} initialContent={noteEditor.content} onClose={() => setNoteEditor(null)} onSave={saveNote} /> : null}
      {suggestionReview ? <SuggestionReview source={suggestionReview.source} organization={updatedOrganization} suggestions={suggestionReview.suggestions} onClose={() => setSuggestionReview(null)} onApply={applySuggestions} /> : null}
      {sectionSuggestionReview ? <SectionSuggestionReview section={sectionSuggestionReview.section} entries={sectionSuggestionReview.entries} organization={updatedOrganization} onClose={() => setSectionSuggestionReview(null)} onApply={applySectionSuggestions} /> : null}
      {sourcePendingDeletion ? <KnowledgeSourceDeleteDialog source={sourcePendingDeletion} onClose={() => setSourcePendingDeletion(null)} onConfirm={removeSource} /> : null}
    </div>
  );
}


export default KnowledgeBase;
